import { CARTOPOUSSE_BASE_URL } from '../utils/ApiUrls';

class ActivityService {
  // Create a new activity with associated risks
  async createActivity(activityData, accessToken) {
    try {
      console.log('📡 activityService.createActivity - Sending data:', activityData);
      
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/activities/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(activityData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Activity creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch all activities for the user's organization with filtering
  async getOrganizationActivities(accessToken, filters = {},pagination_data) {
    try {
      console.log('📡 activityService.getOrganizationActivities - Received filters:', filters);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.activity_name) {
        queryParams.append('activity_name', filters.activity_name);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }
      if (pagination_data.page) {
        queryParams.append('page', pagination_data.page);
      }
      if (pagination_data.page_size) {
        queryParams.append('page_size', pagination_data.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${CARTOPOUSSE_BASE_URL}/activities/${queryString ? `?${queryString}` : ''}`;
      
      console.log('📡 activityService.getOrganizationActivities - Final URL:', url);
      console.log('📡 activityService.getOrganizationActivities - Query params:', queryString);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching organization activities',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch activities created by the current user with filtering
  async getMyActivities(accessToken, filters = {}, pagination_data) {
    try {
      console.log('📡 activityService.getMyActivities - Received filters:', filters);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.activity_name) {
        queryParams.append('activity_name', filters.activity_name);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (pagination_data.page) {
        queryParams.append('page', pagination_data.page);
      }
      if (pagination_data.page_size) {
        queryParams.append('page_size', pagination_data.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${CARTOPOUSSE_BASE_URL}/activities/my-activities/${queryString ? `?${queryString}` : ''}`;
      
      console.log('📡 activityService.getMyActivities - Final URL:', url);
      console.log('📡 activityService.getMyActivities - Query params:', queryString);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching your activities',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Fetch details for a single activity
  async getActivityDetails(activityId, accessToken) {
    try {
      console.log('📡 activityService.getActivityDetails - Activity ID:', activityId);
      
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/activities/${activityId}/`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching activity details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Edit/Update activity
  async editActivity(activityId, activityData, accessToken) {
    try {
      console.log('📡 activityService.editActivity - Activity ID:', activityId);
      console.log('📡 activityService.editActivity - Sending data:', activityData);
      
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/activities/${activityId}/edit/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(activityData)
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Activity update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Alias for updateActivity to maintain compatibility
  async updateActivity(activityId, activityData, accessToken) {
    return this.editActivity(activityId, activityData, accessToken);
  }

  // Delete activity
  async deleteActivity(activityId, accessToken) {
    try {
      console.log('📡 activityService.deleteActivity - Activity ID:', activityId);
      
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/activities/${activityId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Activity deletion failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // Search activities for autocomplete (used in process forms)
  async searchActivities(query, accessToken) {
    try {
      console.log('📡 activityService.searchActivities - Query:', query);
      
      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(`${CARTOPOUSSE_BASE_URL}/activities/search/?q=${encodedQuery}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Activity search failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }
}

// Export a single instance
const activityService = new ActivityService();
export default activityService;
